import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../config";
import {
  Search, Loader2, RefreshCw, ExternalLink, CheckCircle2, AlertCircle,
  Send, FileText, Globe2, Clock,
} from "lucide-react";

/**
 * AdminSeoIndexingTab — fast-ping Bing/Yandex/Seznam after sitemap changes.
 *
 * IndexNow handles Bing + Yandex + Seznam in a single submission.
 * Google deprecated sitemap-ping in 2023; we provide a deep link to
 * Search Console so admin can re-submit in two clicks.
 *
 * One-click action: "Resubmit all" fans out the latest published blog
 * URLs to IndexNow + pings Bing's legacy sitemap endpoint. Submissions
 * are logged in Mongo for audit.
 */

function adminHeaders() {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "{}");
    return u.email ? { "X-User-Email": u.email } : {};
  } catch {
    return {};
  }
}

export default function AdminSeoIndexingTab() {
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resubmitting, setResubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const [s, h] = await Promise.all([
        axios.get(`${API}/admin/seo-indexing/status`, { headers: adminHeaders() }),
        axios.get(`${API}/admin/seo-indexing/history?limit=20`, { headers: adminHeaders() }),
      ]);
      setStatus(s.data);
      setHistory(h.data?.items || []);
    } catch (e) {
      console.error("seo-indexing load failed", e);
      setError(e?.response?.data?.detail || "Načítanie zlyhalo.");
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [load]);

  const resubmitAll = useCallback(async () => {
    setResubmitting(true);
    setError("");
    try {
      const r = await axios.post(
        `${API}/admin/seo-indexing/resubmit-all`,
        {},
        { headers: adminHeaders() },
      );
      setLastResult(r.data);
      load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Resubmit zlyhal.");
    }
    setResubmitting(false);
  }, [load]);

  return (
    <div data-testid="admin-seo-indexing-tab" className="space-y-6">
      <header>
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Search className="w-6 h-6 text-emerald-600" />
          Search Engine Indexing
        </h2>
        <p className="text-sm text-gray-500 mt-1 max-w-2xl">
          Po každej zmene sitemapy (nový blog post, preložené verzie) klikni
          <strong> Resubmit all</strong> aby IndexNow notifikoval{" "}
          <strong>Bing, Yandex, Seznam a Naver</strong> — re-index za 24–48 hodín
          namiesto 2–3 týždňov. Google ping API zrušil — link na Search Console nižšie.
        </p>
      </header>

      {/* One-click action */}
      <section className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-2xl p-6 lg:p-8">
        <div className="flex items-start gap-5 flex-wrap">
          <div className="flex-1 min-w-[260px]">
            <h3 className="text-xl font-bold mb-1">One-click resubmit</h3>
            <p className="text-sm text-emerald-100 mb-3">
              Pošle všetky publikované blog posty na IndexNow (Bing/Yandex/Seznam)
              + pingne Bing sitemap endpoint.
            </p>
            {status?.sitemap_url && (
              <a
                href={status.sitemap_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs font-mono text-emerald-100 hover:text-white"
              >
                <ExternalLink className="w-3 h-3" />
                {status.sitemap_url}
              </a>
            )}
          </div>
          <button
            type="button"
            onClick={resubmitAll}
            disabled={resubmitting}
            data-testid="seo-resubmit-btn"
            className="px-6 py-3 bg-white text-emerald-700 rounded-lg text-sm font-bold hover:bg-emerald-50 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
          >
            {resubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Resubmitting...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Resubmit all
              </>
            )}
          </button>
        </div>

        {lastResult && (
          <div
            data-testid="seo-resubmit-result"
            className="mt-5 space-y-3"
          >
            <EngineResultCard kind="IndexNow" result={lastResult.indexnow} />
            {lastResult.indexnow?.status === 422 && (
              <div className="bg-amber-500/20 rounded-lg p-3 text-xs text-amber-50">
                <strong>HTTP 422 v dev/preview</strong> je očakávaný —
                IndexNow ku key file pristupuje cez produkčný host{" "}
                <code>toprankpulse.com</code>. Po nasadení do produkcie
                bude key file dostupný a engine prijme submisie.
              </div>
            )}
          </div>
        )}
      </section>

      {error && (
        <div
          data-testid="seo-error"
          className="flex items-start gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700"
        >
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* Status grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <StatusCard
          title="IndexNow (Bing/Yandex/Seznam/Naver)"
          icon={<Globe2 className="w-5 h-5" />}
          item={status?.indexnow}
          loading={loading}
        />
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2 mb-2">
            <FileText className="w-5 h-5 text-blue-600" />
            Google Search Console
          </h4>
          <p className="text-xs text-gray-500 mb-3">
            Google ping API už neexistuje (zrušený 2023). Klikni a manuálne
            potvrď re-submit (2 kliky).
          </p>
          {status?.google_search_console_url && (
            <a
              href={status.google_search_console_url}
              target="_blank"
              rel="noreferrer"
              data-testid="seo-gsc-link"
              className="inline-flex items-center gap-1.5 text-xs font-bold text-blue-700 hover:text-blue-800"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Otvoriť GSC sitemaps
            </a>
          )}
        </div>
      </div>

      {/* Key file note */}
      {status?.key_file_url && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
          <p className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
            IndexNow key file
          </p>
          <a
            href={status.key_file_url}
            target="_blank"
            rel="noreferrer"
            data-testid="seo-key-file-link"
            className="text-xs font-mono text-purple-700 hover:underline break-all"
          >
            {status.key_file_url}
          </a>
          <p className="text-[11px] text-gray-500 mt-1">
            Tento súbor overuje, že vlastníš doménu pre IndexNow. Mal by vrátiť kľúč ako text.
          </p>
        </div>
      )}

      {/* History */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Posledné submisie ({history.length})
          </h3>
          <button
            type="button"
            onClick={load}
            data-testid="seo-history-refresh"
            className="text-xs text-gray-500 hover:text-gray-900 flex items-center gap-1"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Obnoviť
          </button>
        </div>
        {history.length === 0 ? (
          <p className="text-sm text-gray-400 py-6 text-center">
            Zatiaľ žiadne submisie. Klikni „Resubmit all" hore.
          </p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {history.map((h) => (
              <li
                key={h.id}
                data-testid={`seo-history-${h.id}`}
                className="py-2.5 flex items-center justify-between gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                    {h.result?.ok ? (
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                    ) : (
                      <AlertCircle className="w-3.5 h-3.5 text-amber-600" />
                    )}
                    <span>{h.kind === "indexnow" ? "IndexNow" : "Bing ping"}</span>
                    <span className="text-[11px] font-mono text-gray-400">
                      HTTP {h.result?.status ?? "?"}
                    </span>
                  </div>
                  <p className="text-[11px] text-gray-500 mt-0.5">
                    {h.url_count} URL · {new Date(h.created_at).toLocaleString("sk-SK")}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function StatusCard({ title, icon, item, loading }) {
  const ok = item?.result?.ok;
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5">
      <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2 mb-3">
        <span className={ok ? "text-emerald-600" : "text-gray-400"}>{icon}</span>
        {title}
      </h4>
      {loading ? (
        <div className="text-xs text-gray-400">Načítavam...</div>
      ) : item ? (
        <>
          <div className="flex items-center gap-2 mb-2">
            {ok ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            ) : (
              <AlertCircle className="w-4 h-4 text-amber-600" />
            )}
            <span className="text-xs font-bold uppercase tracking-wider text-gray-700">
              {ok ? "Úspech" : "Zlyhanie"}
            </span>
            <span className="text-[11px] font-mono text-gray-400 ml-auto">
              HTTP {item.result?.status}
            </span>
          </div>
          <p className="text-[11px] text-gray-500">
            {item.url_count} URL · {new Date(item.created_at).toLocaleString("sk-SK")}
          </p>
        </>
      ) : (
        <div className="text-xs text-gray-400">Ešte nebolo spustené.</div>
      )}
    </div>
  );
}

function EngineResultCard({ kind, result }) {
  const ok = result?.ok;
  return (
    <div className={`rounded-lg p-3 ${ok ? "bg-emerald-500/20" : "bg-red-500/20"}`}>
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider">
        {ok ? (
          <CheckCircle2 className="w-3.5 h-3.5" />
        ) : (
          <AlertCircle className="w-3.5 h-3.5" />
        )}
        {kind} · HTTP {result?.status}
      </div>
      <p className="text-[11px] mt-1 opacity-80">
        {result?.count} URL · {ok ? "OK" : (result?.error || "fail")}
      </p>
    </div>
  );
}
